import { memo } from 'react'
import { useTranslation } from 'react-i18next'
import { PanelLeftClose, PanelLeft, FolderPlus, Upload } from 'lucide-react'
import SelectionToolbar from './Toolbar/SelectionToolbar'
import SortControls from './Toolbar/SortControls'
import ViewToggle from './Toolbar/ViewToggle'
import { AdvancedFilterBar } from './Toolbar/AdvancedFilterBar'
import { AppliedFilters, FilterField } from './Toolbar/filterTypes'
import { Pagination as PaginationData } from '@features/file-explorer/types'

interface FileExplorerToolbarMobileProps {
  selectedCount: number
  totalCount: number
  pagination: PaginationData
  sortBy: string
  sortDirection: 'asc' | 'desc'
  filters: AppliedFilters
  filterFields: FilterField[]
  viewMode: 'table' | 'grid'
  loadingElement: string | null
  isSidebarOpen: boolean
  onToggleSidebar: () => void
  onClearSelection: () => void
  onSelectAll: () => void
  onBulkDownload: () => void
  onBulkDelete: () => void
  onSortChange: (sortBy: string) => void
  onDirectionToggle: () => void
  onApplyFilters: (filters: AppliedFilters) => void
  onViewChange: (view: 'table' | 'grid') => void
  onCreateFolder: () => void
  onUploadDocument: () => void
}

function FileExplorerToolbarMobile({
  selectedCount,
  totalCount,
  pagination,
  sortBy,
  sortDirection,
  filters,
  filterFields,
  viewMode,
  loadingElement,
  isSidebarOpen,
  onToggleSidebar,
  onClearSelection,
  onSelectAll,
  onBulkDownload,
  onBulkDelete,
  onSortChange,
  onDirectionToggle,
  onApplyFilters,
  onViewChange,
  onCreateFolder,
  onUploadDocument
}: FileExplorerToolbarMobileProps) {
  const { t } = useTranslation()

  return (
    <div className="bg-base-100 p-2 sm:p-4">
      {/* Mobile: Stacked Layout */}
      <div className="flex flex-col gap-3 md:hidden">
        {/* Row 1: Toggle + Count + Actions */}
        <div className="flex items-center justify-between gap-2">
          <button
            onClick={onToggleSidebar}
            className="btn btn-sm btn-ghost"
            title={isSidebarOpen ? t('fileExplorer.folderTree.hideFolderTree') : t('fileExplorer.folderTree.showFolderTree')}
          >
            {isSidebarOpen ? (
              <PanelLeftClose className="w-4 h-4" />
            ) : (
              <PanelLeft className="w-4 h-4" />
            )}
          </button>
          <div className="text-xs font-medium text-base-content/70">
            {pagination.from}-{pagination.to}/{pagination.count}
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={onCreateFolder}
              className="btn btn-sm btn-soft btn-secondary"
              title={t('folders.create_button')}
            >
              <FolderPlus className="w-4 h-4" />
            </button>
            <button
              onClick={onUploadDocument}
              className="btn btn-sm btn-primary"
              title={t('documents.upload_button')}
              data-testid="upload-document-button"
            >
              <Upload className="w-4 h-4" />
            </button>
          </div>
        </div>
        {/* Row 2: Selection */}
        {selectedCount > 0 && (
          <SelectionToolbar
            selectedCount={selectedCount}
            totalCount={totalCount}
            onClearSelection={onClearSelection}
            onSelectAll={onSelectAll}
            onBulkDownload={onBulkDownload}
            onBulkDelete={onBulkDelete}
          />
        )}
        {/* Row 3: Filter */}
        <div className="flex items-center">
          <AdvancedFilterBar
            filterFields={filterFields}
            initialFilters={filters}
            onApplyFilters={onApplyFilters}
            isLoading={loadingElement === 'filter'}
          />
        </div>
        {/* Row 4: Sort + View */}
        <div className="flex items-center justify-between gap-2">
          <SortControls
            sortBy={sortBy}
            sortDirection={sortDirection}
            onSortChange={onSortChange}
            onDirectionToggle={onDirectionToggle}
            loadingSortField={loadingElement === 'sort-field'}
            loadingDirection={loadingElement === 'sort-direction'}
          />
          <ViewToggle view={viewMode} onViewChange={onViewChange} />
        </div>
      </div>
    </div>
  )
}

// Custom comparison to prevent unnecessary re-renders
function arePropsEqual(prevProps: FileExplorerToolbarMobileProps, nextProps: FileExplorerToolbarMobileProps) {
  return (
    prevProps.selectedCount === nextProps.selectedCount &&
    prevProps.totalCount === nextProps.totalCount &&
    prevProps.pagination === nextProps.pagination &&
    prevProps.sortBy === nextProps.sortBy &&
    prevProps.sortDirection === nextProps.sortDirection &&
    prevProps.filters === nextProps.filters &&
    prevProps.viewMode === nextProps.viewMode &&
    prevProps.loadingElement === nextProps.loadingElement &&
    prevProps.isSidebarOpen === nextProps.isSidebarOpen
  )
}

export default memo(FileExplorerToolbarMobile, arePropsEqual)
